// Translation catalogs for the shipped UI languages.
//
// Keys are flat, dot-namespaced strings. Every locale MUST define the same set
// of keys as `en` (enforced by messages.test.js) so a missing translation is a
// test failure, not a silent English leak at runtime.
//
// Interpolation: `{name}` style placeholders are filled by i18n.t(key, params).
// API field names (trackId/bundleId) are intentionally left untranslated.
import { STOREFRONTS } from '../data/storefronts.js';
import { simplifyCatalog } from './simplifiedChinese.js';

/** @typedef {Record<string, string>} Catalog */

export const STOREFRONT_COUNT = STOREFRONTS.length;

const storefrontCountryMessages = Object.freeze(Object.fromEntries(
  STOREFRONTS.map(({ code, name }) => [`country.${code}`, name]),
));

/** @type {Catalog} */
const en = {
  'coverage.countryCount': '{countryCount}',
  'tagline': 'Across-country App Store storefront comparison for developers. Compare any app across {countryCount} App Store countries by ratings, chart rank, price and market opportunity. Free, instant, no login.',
  'brand.name': 'App Radar',
  'brand.tag': 'App Store market radar',
  'brand.homeAria': 'App Radar — home',
  'lang.label': 'Language',
  'theme.label': 'Theme',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'nav.compare': 'Compare',
  'nav.radar': 'Charts',
  'nav.opportunities': 'Opportunities',
  'nav.dailyInsight': 'Daily report',

  'search.label': 'App name',
  'search.placeholder': 'Search an app, e.g. Yelp',
  'search.button': 'Search',
  'search.region.label': 'Search storefront',
  'search.region.auto': 'Auto (by language)',
  'search.genre.label': 'Category',
  'search.genre.all': 'All categories',
  'search.genre.active': '{genre}',
  'search.platform.label': 'Platform',
  'search.platform.ios': 'iOS / iPadOS',
  'search.platform.mac': 'macOS',

  'status.searching': 'Searching for “{query}”…',
  'status.loadingStorefronts': 'Loading {count} storefronts for “{name}”…',
  'status.loadingProgress': 'Loading {current}/{total} storefronts…',
  'status.openingShared': 'Opening shared comparison…',
  'status.checkingCharts': 'Checking top-chart ranks…',
  'status.idle': 'Search an app above to compare it across Apple storefronts.',
  'offline.cachedBanner': 'Offline — showing last cached view.',

  'error.searchFailed': 'Search failed: {message}. Check your connection and try again.',
  'error.noResult': 'No App Store result found for “{query}”. Try a different name.',
  'error.loadById': 'Could not load app {appId}: {message}.',
  'error.noAppId': 'No App Store app found for id {appId}.',

  'footer.honesty': 'Honesty note: Apple does not publish public download counts, and this app never estimates them. It uses public proxy signals only — average rating, rating count, price, availability, and Apple’s published chart snapshots where available. Normal comparison works without login; the opportunities page reads a generated daily cache.',
  'footer.attribution': 'Data from the Apple App Store.',
  'footer.notAffiliated': 'Not affiliated with Apple Inc.',
  'footer.localFirst': 'Local-first · everything runs in your browser',
  'footer.version': 'Build',

  'col.region': 'Region',
  'col.rating': 'Rating',
  'col.ratingCount': 'Rating count',
  'col.ratingDeviation': 'Rating vs avg',
  'col.price': 'Price',
  'col.chartRank': 'Chart rank',
  'col.popularityScore': 'Popularity',
  'col.highlights': 'Highlights',
  'col.store': 'Store',

  'cell.free': 'Free',
  'cell.open': 'Open ↗',
  'cell.unavailable': 'Not available in this storefront',
  'cell.lookupError': 'Lookup error: {message}',

  'tag.rating-count-leader': 'Most traction',
  'tag.rating-count-low': 'Least traction',
  'tag.rating-leader': 'Top rated',
  'tag.rating-low': 'Lowest rated',
  'tag.emerging': 'Emerging',
  'tag.low-visibility': 'Low visibility',
  'tag.price-mismatch': 'Price outlier',
  'tag.localization-gap': 'missing {language}',
  'tag.localization-gap.tooltip': 'Apple languageCodesISO2A does not list {language} for this storefront.',
  'tag.unavailable': 'Unavailable',
  'opportunity.label': 'Early mover',
  'opportunity.tooltip': 'Charting in top 10 with fewer than 50 ratings — potential early-mover window',

  'insight.most': 'Most traction: {region}',
  'insight.weakest': 'Weakest: {region}',
  'insight.suffix': 'Rating count is a popularity proxy, not downloads.',
  'insight.none': 'No rating data available to compare across regions.',

  'chartNote.enabled': 'Chart rank is Apple’s published top-free / top-paid position — not a download count.',
  'chartNote.disabled': 'Chart rank is disabled in this static build: Apple’s RSS feeds are not browser-accessible (CORS). Enable the bundled proxy to show live ranks (see README).',
  'chart.disabledTooltip': 'Live chart ranks need the RSS proxy — disabled in this build (see config.js)',
  'chart.notLoaded': 'Chart rank not loaded yet',
  'chart.unavailableTooltip': 'Apple RSS feed could not be read',

  'table.caption': "Per-region comparison. The bar shows each region's rating count as a share of the highest shown — a popularity proxy, not downloads. Apple does not expose download counts.",
  'proxyBar.title': '{pct}% of the highest rating count shown',

  ...storefrontCountryMessages,
  'country.us': 'United States',
  'country.de': 'Germany',
  'country.gb': 'United Kingdom',
  'country.ca': 'Canada',
  'country.au': 'Australia',
  'country.fr': 'France',
  'country.it': 'Italy',
  'country.es': 'Spain',
  'country.nl': 'Netherlands',
  'country.se': 'Sweden',
  'country.pl': 'Poland',
  'country.ru': 'Russia',
  'country.jp': 'Japan',
  'country.kr': 'South Korea',
  'country.cn': 'China mainland',
  'country.hk': 'Hong Kong',
  'country.tw': 'Taiwan',
  'country.sg': 'Singapore',
  'country.id': 'Indonesia',
  'country.th': 'Thailand',
  'country.my': 'Malaysia',
  'country.in': 'India',
  'country.sa': 'Saudi Arabia',
  'country.ae': 'UAE',
  'country.br': 'Brazil',
  'country.mx': 'Mexico',
  'country.ar': 'Argentina',
  'country.za': 'South Africa',

  'ranking.title': 'Top Markets by Rating Count',
  'leaderboard.title': 'Your Recent Searches',
  'watchlist.title': 'Saved Apps',
  'watchlist.pin': '☆ Save',
  'watchlist.unpin': '★ Saved',

  'leaders.panelTitle': 'Category Leaders — {genre}',
  'leaders.selectCountry': 'Select country',
  'leaders.loading': 'Loading category charts…',
  'leaders.loadError': 'Could not load category chart data.',
  'leaders.noData': 'No data available.',
  'leaders.corsNote': 'Live category charts need the optional Apple RSS proxy, which is off by default on this static build (see README). Proxy configured → real leaders load.',
  'leaders.topFree': 'Top 5 Free',
  'leaders.topPaid': 'Top 5 Paid',

  'export.csv': 'Export CSV',
  'export.report': 'Download Report (JSON)',
  'export.reportPrint': 'Print Report',

  'developer.otherApps': 'More apps by {developer}',
  'developer.compare': 'Compare',
  'developer.compareAriaLabel': 'Compare {name}',

  'compare.toggle': '+ Compare another app',
  'compare.toggleClose': '− Close comparison',
  'compare.placeholder': 'Second app name or ID',
  'compare.button': 'Compare',
  'compare.loading': 'Loading second app…',
  'compare.clear': 'Clear',
  'compare.col.appA': 'App A',
  'compare.col.appB': 'App B',
  'compare.col.ratingA': 'Rating A',
  'compare.col.ratingCountA': 'Reviews A',
  'compare.col.ratingB': 'Rating B',
  'compare.col.ratingCountB': 'Reviews B',
  'compare.unavailable': 'N/A',

  'a11y.skipToMain': 'Skip to main content',
  'a11y.opportunityAriaLabel': 'Early mover opportunity: charting in top 10 with fewer than 50 ratings',
  'a11y.ratingAbove': '{value} points above global average rating',
  'a11y.ratingBelow': '{value} points below global average rating',
  'a11y.openStoreAriaLabel': 'Open {country} App Store listing',
  'a11y.watchlistCard': 'Load comparison for {name}',

  'versionNotes': 'Version Notes',
  'allSameVersion': 'All storefronts running v{version}',
  'versionDifferences': 'Version differences across storefronts:',
  'staggeredRollout': 'Staggered rollout',
  'staggeredRolloutDetail': 'Most storefronts on v{version}; {count} on a different build.',
  'releaseNotesDiff': 'Release Notes Differences',
  'releaseNotesCommon': 'Most storefronts: {notes}',

  'explore.title': 'Explore',
  'explore.subtitle': 'Tap any card to compare across {countryCount} storefronts.',
  'explore.theme.global': 'Global Giants',
  'explore.theme.regional': 'Big in One Region',
  'explore.theme.insight': 'Interesting Angles',
  'explore.cardAriaLabel': 'Explore {name} across storefronts',

  'delta.up': '▲{n}',
  'delta.down': '▼{n}',
  'delta.ariaUp': 'up {n} vs your check on {date}',
  'delta.ariaDown': 'down {n} vs your check on {date}',
  'delta.firstLook': 'First look — check back to see movement.',

  'changes.title': 'Changes since your last visit',
  'changes.dismiss': 'Dismiss',
  'changes.version': 'Version updated in {country}: {prev} → {current}',
  'changes.priceDown': 'Price dropped in {country}: {prev} → {current}',
  'changes.priceUp': 'Price increased in {country}: {prev} → {current}',

  'digest.title': 'Weekly Movers',
  'digest.since': 'vs {date}',
  'digest.countriesUp': '{n} countries ↑',
  'digest.countriesDown': '{n} countries ↓',

  'marketGap.title': 'Chart Penetration',
  'marketGap.free': 'Top-free: {n}/{total} countries',
  'marketGap.paid': 'Top-paid: {n}/{total} countries',
  'marketGap.grossing': 'Top-grossing: {n}/{total} countries',
  'marketGap.notCharting': 'Not charting in these major markets:',
  'marketGap.allMajorCharting': 'Charting in all tracked major markets.',

  'marketOpp.title': 'Market Opportunity',
  'marketOpp.subtitle': 'Where to focus next — storefronts ranked by a blended signal of demand, localization headroom and momentum.',
  'marketOpp.note': 'Heuristic built from Apple\'s public signals only — not a download or revenue estimate.',
  'marketOpp.itemAriaLabel': 'Rank {rank}: {country}, opportunity score {score} of 100, leading factor {factor}.',
  'marketOpp.factor.demand': 'Demand',
  'marketOpp.factor.localization': 'Localization gap',
  'marketOpp.factor.momentum': 'Momentum',
  'marketOpp.factor.visibility': 'Chart visibility',
  'marketOpp.factor.volatility': 'Volatility',

  'opportunityRadar.eyebrow': 'Opportunity Radar',
  'opportunityRadar.title': 'Find chart-driven app opportunities',
  'opportunityRadar.subtitle': 'Review opportunity candidates generated from the daily App Store chart cache. This page is read-only for now and does not refresh charts in realtime.',
  'opportunityRadar.primaryTargets': 'Focus set',
  'opportunityRadar.secondaryTargets': 'Extended set',
  'opportunityRadar.targetsAria': 'Opportunity Radar market scope',
  'opportunityRadar.loading': 'Loading cached opportunities…',
  'opportunityRadar.refreshing': 'Requesting a cache update. ',
  'opportunityRadar.queued': 'Cache update request queued. ',
  'opportunityRadar.cached': 'Showing the latest generated opportunity cache. ',
  'opportunityRadar.generatedAt': 'Generated {date}.',
  'opportunityRadar.nextAllowedAt': 'Next cache update after {time}.',
  'opportunityRadar.empty': 'No generated opportunity cache yet. This page waits for the daily chart job instead of scanning from the browser.',
  'opportunityRadar.limited': 'Cache update requests are rate-limited right now. Existing generated data remains available.',
  'opportunityRadar.paused': 'Opportunity Radar updates are paused because storage, budget or safety limits are not available.',
  'opportunityRadar.error': 'Could not load opportunities: {message}.',
  'opportunityRadar.retry': 'Retry',
  'opportunityRadar.scanNow': 'Request cache update',
  'opportunityRadar.refresh': 'Request cache update',
  'opportunityRadar.scanning': 'Requesting update…',
  'opportunityRadar.scoreLabel': 'score',
  'opportunityRadar.scoreAria': 'Opportunity score {score} out of 100',
  'opportunityRadar.unnamedApp': 'Unnamed app',
  'opportunityRadar.unknownGenre': 'Unknown category',
  'opportunityRadar.targetReadout': 'Market readout',
  'opportunityRadar.noTargetScores': 'No market scores available.',
  'opportunityRadar.why': 'Why it matters',
  'opportunityRadar.noReasons': 'No explanation available yet.',
  'opportunityRadar.targetNoReason': 'No leading reason yet.',
  'opportunityRadar.factor.sourceHeat': 'Source heat',
  'opportunityRadar.factor.targetCold': 'Coverage gap',
  'opportunityRadar.factor.localizationGap': 'Localization gap',
  'opportunityRadar.factor.substituteWeakness': 'Substitute weakness',
  'opportunityRadar.reason.source-heat': '{market} benefits from strong source-market chart heat.',
  'opportunityRadar.reason.trend-velocity': '{market} is attached to new or rising source-market movement.',
  'opportunityRadar.reason.target-unavailable': '{market} is unavailable or absent in lookup data.',
  'opportunityRadar.reason.target-cold': '{market} has low chart presence and local rating volume.',
  'opportunityRadar.reason.localization-gap': '{market} appears to be missing local storefront language support.',
  'opportunityRadar.reason.weak-substitutes': '{market} has few strong same-category substitutes in the checked chart sample.',
  'opportunityRadar.breakoutsAria': 'Daily breakout candidates',
  'opportunityRadar.breakoutsEyebrow': 'Cached daily breakouts',
  'opportunityRadar.breakoutsTitle': 'Opportunity candidates from the latest chart cache',
  'opportunityRadar.breakoutsEmpty': 'No breakout candidates in the latest daily cache.',
  'opportunityRadar.capturedAt': 'Captured {date}',
  'opportunityRadar.section.hotNewEntries': 'Hot new entries',
  'opportunityRadar.section.fastClimbers': 'Fast climbers',
  'opportunityRadar.section.steadyClimbers': 'Steady climbers',
  'opportunityRadar.section.needsBaseline': 'Needs more baseline',
  'opportunityRadar.section.reviewFlags': 'Review flags',
  'opportunityRadar.baseline.complete': 'Baseline: top100-to-top100',
  'opportunityRadar.baseline.partial': 'Baseline: partial',
  'opportunityRadar.baseline.insufficient': 'Baseline: insufficient',
  'opportunityRadar.baselineDate': 'Baseline {date}',
  'opportunityRadar.baselineMissingDate': 'No previous baseline',
  'opportunityRadar.partialBaselineWarning': 'From tomorrow, top100-to-top100 comparisons become more reliable.',
  'opportunityRadar.insufficientBaselineWarning': 'Wait for a prior top100 snapshot before treating new-entry labels as confirmed.',
  'opportunityRadar.methodology.complete': 'The current chart was compared with the previous complete top-100 chart. New entries were absent from that prior chart.',
  'opportunityRadar.methodology.partial': 'These candidates come from top-100 chart movement, but the previous baseline only covered the top 25. Missing history cannot be treated as confirmed new entries yet.',
  'opportunityRadar.methodology.insufficient': 'There is not enough prior chart history yet. Treat these candidates as directional until a complete prior top-100 chart exists.',
  'opportunityRadar.field.market': 'Market',
  'opportunityRadar.field.chartType': 'Chart',
  'opportunityRadar.field.rankMovement': 'Rank movement',
  'opportunityRadar.field.capturedDate': 'Captured',
  'opportunityRadar.field.reviewHint': 'Review hint',
  'opportunityRadar.field.filterStatus': 'Filter status',
  'opportunityRadar.field.filterReasons': 'Filter reasons',
  'opportunityRadar.kind.hotNewEntry': 'Hot new entry',
  'opportunityRadar.kind.fastClimber': 'Fast climber',
  'opportunityRadar.kind.steadyClimber': 'Steady climber',
  'opportunityRadar.kind.multiWindowClimber': 'Multi-window climber',
  'opportunityRadar.kind.newEntry': 'New top100 entry',
  'opportunityRadar.chartType.topFree': 'Top Free',
  'opportunityRadar.chartType.topPaid': 'Top Paid',
  'opportunityRadar.chartType.topGrossing': 'Top Grossing',
  'opportunityRadar.rank.newTo': 'New to {current}',
  'opportunityRadar.rank.moved': '{previous} to {current}{gain}',
  'opportunityRadar.reviewHint.baseline': 'Review after a full top100 baseline is available.',
  'opportunityRadar.reviewHint.lookup': 'Lookup enrichment or rating volume needs review.',
  'opportunityRadar.reviewHint.fastClimber': 'Check the climb against category context.',
  'opportunityRadar.reviewHint.steadyClimber': 'Review sustained movement across the 24h and 48h windows.',
  'opportunityRadar.reviewHint.filtered': 'Keep as a raw signal; do not treat as a clear opportunity.',
  'opportunityRadar.reviewHint.downranked': 'Review manually before comparing as an opportunity.',
  'opportunityRadar.reviewHint.needsReview': 'Needs more cached context before opportunity review.',
  'opportunityRadar.reviewHint.ready': 'Ready for manual comparison review.',
  'opportunityRadar.filterStatus.candidate': 'Candidate',
  'opportunityRadar.filterStatus.downranked': 'Downranked',
  'opportunityRadar.filterStatus.filtered': 'Filtered',
  'opportunityRadar.filterStatus.needs_review': 'Needs review',
  'opportunityRadar.filterCategory.government': 'Government',
  'opportunityRadar.filterCategory.banking': 'Banking or regulated finance',
  'opportunityRadar.filterCategory.local_transit': 'Local transit',
  'opportunityRadar.filterCategory.ticketing': 'Ticketing',
  'opportunityRadar.filterCategory.regional_service': 'Regional service',
  'opportunityRadar.filterCategory.seasonal_event': 'Seasonal event',
  'opportunityRadar.filterCategory.large_platform': 'Large platform or media',
  'opportunityRadar.filterCategory.low_confidence': 'Low confidence',
  'opportunityRadar.filterReason.government-or-public-service': 'Government or public service apps are strongly local-bound.',
  'opportunityRadar.filterReason.regulated-finance-or-identity': 'Banking, insurance, tax or identity apps need regulated-market review.',
  'opportunityRadar.filterReason.local-transit-or-parking': 'Local transit, parking or toll apps may not transfer across markets.',
  'opportunityRadar.filterReason.ticketing-or-local-admission': 'Ticketing and admission apps are often tied to local inventory.',
  'opportunityRadar.filterReason.region-bound-service': 'Regional services may reflect local coverage instead of a global product gap.',
  'opportunityRadar.filterReason.large-platform-or-media': 'Large platforms or media apps can dominate charts without implying an indie opportunity.',
  'opportunityRadar.filterReason.seasonal-or-event-bound': 'Seasonal or event-bound spikes can decay quickly.',
  'opportunityRadar.filterReason.low-confidence-missing-genre-or-rating': 'Cached chart data lacks enough genre or rating context.',
  'opportunityRadar.filterReason.none': 'No filter reason recorded.',
  'opportunityRadar.unknownMarket': 'Unknown market',
  'opportunityRadar.unknownChartType': 'Unknown chart',
  'opportunityRadar.dailyReportEyebrow': 'Daily Dogfood Report',

  'hero.eyebrow': 'For developers comparing markets',
  'hero.headline': 'Compare any app across',
  'hero.headlineEm': 'country App Stores',
  'hero.sub': 'Search one iOS or macOS app and compare ratings, chart position, price, availability and opportunity signals across {countryCount} storefronts, built for developers deciding where to localize or launch next.',
  'features.eyebrow': 'Feature highlights',
  'features.title': 'A cross-country read on App Store demand',
  'features.subtitle': 'Use public Apple signals to compare markets before you choose a launch, localization or growth bet.',
  'features.coverage.title': 'Storefront coverage',
  'features.coverage.body': 'Compare the same app across {countryCount} countries with ratings, price and availability aligned in one table.',
  'features.traction.title': 'Traction signals',
  'features.traction.body': 'Use rating count, rating deviation and chart rank as directional demand signals without pretending they are downloads.',
  'features.opportunity.title': 'Market opportunity',
  'features.opportunity.body': 'Spot countries where visibility, demand and localization headroom point to a stronger next-market candidate.',
  'features.charts.title': 'Chart discovery',
  'features.charts.body': 'Browse current category charts by country, then send any app into the full comparison workflow.',
  'features.changes.title': 'Change monitoring',
  'features.changes.body': 'Revisit an app to see locally saved version and price changes across storefronts.',
  'features.export.title': 'Shareable evidence',
  'features.export.body': 'Copy a comparison link or export CSV when you need to brief a product, marketing or localization decision.',
  'trust.ariaLabel': 'Why you can trust this tool',
  'trust.noLogin': 'No login',
  'trust.local': 'Runs in your browser',
  'trust.publicData': 'Public Apple data only',
  'trust.free': 'Free to use',

  'categoryTrending.title': 'Category Breakdown',
  'categoryTrending.summary': '{genre} leads this chart ({topCount}/{totalCount}, {topPct}%); {distinctGenres} categories represented.',
  'categoryTrending.countLabel': 'Count',
  'categoryTrending.percentLabel': 'Share',
  'categoryTrending.rowAriaLabel': '{genre}: {count} of {totalCount} ({pct}%)',

  'chartBrowser.browseButton': 'Browse Charts',
  'chartBrowser.title': 'Browse Charts',
  'chartBrowser.subtitle': 'See what\'s trending in any country — click any app to compare it across all {countryCount} storefronts.',
  'chartBrowser.sampleSnapshotNote': 'Chart snapshot from {date}.',
  'chartBrowser.countryLabel': 'Country',
  'chartBrowser.chartTypeLabel': 'Chart type',
  'chartBrowser.type.top-free': 'Top Free',
  'chartBrowser.type.top-paid': 'Top Paid',
  'chartBrowser.type.top-grossing': 'Top Grossing',
  'chartBrowser.genreLabel': 'Genre',
  'chartBrowser.genreAll': 'All genres',
  'chartBrowser.hint': 'Select a country to load the current chart.',
  'chartBrowser.loading': 'Loading chart…',
  'chartBrowser.error': 'Could not load chart: {message}',
  'chartBrowser.retry': 'Retry',
  'chartBrowser.empty': 'No chart data returned for this country.',
  'chartBrowser.cardAriaLabel': '#{rank} {name} — click to compare across storefronts',
  'chartBrowser.cardAriaLabelGenre': '#{rank} {name}, {genre} — click to compare across storefronts',
  'chartBrowser.compareAgainstCurrent': 'Compare against current',
  'chartBrowser.compareAgainstCurrentAriaLabel': 'Compare {name} against the current app',
  'chartBrowser.sortLabel': 'Sort by',
  'chartBrowser.sort.rank': 'Chart rank',
  'chartBrowser.sort.name': 'App name',
  'chartBrowser.sort.artist': 'Developer',
  'chartBrowser.sort.genre': 'Category',
  'chartBrowser.sortDir.asc': 'Ascending ↑',
  'chartBrowser.sortDir.desc': 'Descending ↓',
  'chartBrowser.exportCsv': 'Export CSV',
  'chartBrowser.exportJson': 'Export JSON',
  'chartBrowser.proxyRequired': 'Live chart browsing requires the RSS proxy (Cloudflare Worker) to be deployed. The static build on GitHub Pages cannot fetch Apple\'s RSS feeds directly due to CORS restrictions.',
  'chartBrowser.proxySetupLink': 'Set up the RSS proxy →',
  'chartBrowser.penetration': 'Also charts in {n} of {total} sample countries',
  'chartBrowser.penetrationTitle': 'In your sample: {countries}',
  'chartBrowser.rankDelta.new': 'NEW',
  'chartBrowser.rankDelta.ariaNew': 'New since your check on {date}',
  'chartBrowser.rankDelta.ariaUp': 'Chart rank up {n} since your check on {date}',
  'chartBrowser.rankDelta.ariaDown': 'Chart rank down {n} since your check on {date}',
  'share.copyLabel': 'Share link',
  'share.copyAriaLabel': 'Copy comparison link to clipboard',
  'share.twitterLabel': 'Share on X',
  'share.twitterAriaLabel': 'Share comparison on X (Twitter)',
  'share.copied': 'Copied!',
  'share.twitterText': 'Check out "{appName}" across {countryCount} App Store markets on App Radar',
};

/** @type {Catalog} */
const ja = {
  'coverage.countryCount': '{countryCount}',
  'tagline': '開発者向けの国別 App Store 比較: {countryCount} か国で評価、チャート順位、価格、市場機会を比較。無料、即時、ログイン不要。',
  'brand.name': 'App Radar',
  'brand.tag': '各国 App Store インテリジェンス',
  'brand.homeAria': 'App Radar — ホーム',
  'lang.label': '言語',
  'theme.label': 'テーマ',
  'theme.system': 'システム',
  'theme.light': 'ライト',
  'theme.dark': 'ダーク',
  'nav.compare': '比較',
  'nav.radar': 'チャート',
  'nav.opportunities': '機会',
  'nav.dailyInsight': '日次レポート',

  'search.label': 'アプリ名',
  'search.placeholder': 'アプリを検索（例：Yelp）',
  'search.button': '検索',
  'search.region.label': '検索ストア',
  'search.region.auto': '自動（言語に合わせる）',
  'search.genre.label': 'カテゴリ',
  'search.genre.all': 'すべてのカテゴリ',
  'search.genre.active': '{genre}',
  'search.platform.label': 'プラットフォーム',
  'search.platform.ios': 'iOS / iPadOS',
  'search.platform.mac': 'macOS',

  'status.searching': '「{query}」を検索中…',
  'status.loadingStorefronts': '「{name}」の{count}か国の App Store を読み込み中…',
  'status.loadingProgress': '{total}か国中{current}か国を読み込み中…',
  'status.openingShared': '共有された比較を開いています…',
  'status.checkingCharts': 'トップチャート順位を確認中…',
  'status.idle': '上の検索欄からアプリを検索して、各国の App Store で比較しましょう。',
  'offline.cachedBanner': 'オフライン — 最後に保存された表示を表示しています。',

  'error.searchFailed': '検索に失敗しました：{message}。接続を確認してもう一度お試しください。',
  'error.noResult': '「{query}」に一致する App Store の結果が見つかりませんでした。別の名前でお試しください。',
  'error.loadById': 'アプリ {appId} を読み込めませんでした：{message}。',
  'error.noAppId': 'ID {appId} の App Store アプリが見つかりませんでした。',

  'footer.honesty': '正直なお断り：Apple はダウンロード数を公開しておらず、本アプリも一切推定しません。利用するのは公開された代理指標のみ — 平均評価、評価件数、価格、提供状況、利用可能な公開チャートスナップショットです。通常の比較はログインなしで動作し、機会ページは生成済みの日次キャッシュを読み込みます。',
  'footer.attribution': 'データは Apple App Store から取得しています。',
  'footer.notAffiliated': 'Apple Inc. とは提携していません。',
  'footer.localFirst': 'ローカルファースト · すべてブラウザ内で動作します',
  'footer.version': 'ビルド',

  'col.region': '地域',
  'col.rating': '評価',
  'col.ratingCount': '評価件数',
  'col.ratingDeviation': '平均との差',
  'col.price': '価格',
  'col.chartRank': 'チャート順位',
  'col.popularityScore': '人気スコア',
  'col.highlights': 'ハイライト',
  'col.store': 'ストア',

  'cell.free': '無料',
  'cell.open': '開く ↗',
  'cell.unavailable':'このストアでは配信されていません',
  'cell.lookupError': '取得エラー：{message}',

  'tag.rating-count-leader': '最も勢いあり',
  'tag.rating-count-low': '勢い最小',
  'tag.rating-leader': '最高評価',
  'tag.rating-low': '最低評価',
  'tag.emerging': '成長中',
  'tag.low-visibility': '露出が低い',
  'tag.price-mismatch': '価格の外れ値',
  'tag.localization-gap': '{language} なし',
  'tag.localization-gap.tooltip': 'Apple の languageCodesISO2A に、このストア向けの {language} が含まれていません。',
  'tag.unavailable': '配信なし',
  'opportunity.label': '先行者優位',
  'opportunity.tooltip': 'トップ10入りしているにもかかわらずレビューが50件未満 — 先行者のチャンスの可能性あり',

  'insight.most': '最も勢いがある：{region}',
  'insight.weakest': '最も弱い：{region}',
  'insight.suffix': '評価件数は人気の代理指標であり、ダウンロード数ではありません。',
  'insight.none': '各地域を比較できる評価データがありません。',

  'chartNote.enabled': 'チャート順位は Apple が公開するトップ無料／トップ有料の順位であり、ダウンロード数ではありません。',
  'chartNote.disabled': 'このスタティックビルドではチャート順位は無効です：Apple の RSS フィードはブラウザから直接取得できません（CORS）。同梱プロキシを有効にするとライブ順位を表示できます（README 参照）。',
  'chart.disabledTooltip': 'ライブのチャート順位には RSS プロキシが必要です — このビルドでは無効（config.js 参照）',
  'chart.notLoaded': 'チャート順位はまだ読み込まれていません',
  'chart.unavailableTooltip': 'Apple の RSS フィードを読み取れませんでした',

  'table.caption': '地域別の比較。バーは各地域の評価件数を、表示中の最大値に対する割合で示しています — 人気の代理指標であり、ダウンロード数ではありません。Apple はダウンロード数を公開していません。',
  'proxyBar.title': '表示中の最大評価件数の{pct}％',

  ...storefrontCountryMessages,
  'country.us': 'アメリカ',
  'country.de': 'ドイツ',
  'country.gb': 'イギリス',
  'country.ca': 'カナダ',
  'country.au': 'オーストラリア',
  'country.fr': 'フランス',
  'country.it': 'イタリア',
  'country.es': 'スペイン',
  'country.nl': 'オランダ',
  'country.se': 'スウェーデン',
  'country.pl': 'ポーランド',
  'country.ru': 'ロシア',
  'country.jp': '日本',
  'country.kr': '韓国',
  'country.cn': '中国本土',
  'country.hk': '香港',
  'country.tw': '台湾',
  'country.sg': 'シンガポール',
  'country.id': 'インドネシア',
  'country.th': 'タイ',
  'country.my': 'マレーシア',
  'country.in': 'インド',
  'country.sa': 'サウジアラビア',
  'country.ae': 'UAE',
  'country.br': 'ブラジル',
  'country.mx': 'メキシコ',
  'country.ar': 'アルゼンチン',
  'country.za': '南アフリカ',

  'ranking.title': '評価件数トップの市場',
  'leaderboard.title': '最近の検索',
  'watchlist.title': '保存済みアプリ',
  'watchlist.pin': '☆ 保存',
  'watchlist.unpin': '★ 保存済み',

  'leaders.panelTitle': 'カテゴリランキング — {genre}',
  'leaders.selectCountry': '国を選択',
  'leaders.loading': 'カテゴリチャートを読み込み中…',
  'leaders.loadError': 'カテゴリチャートの読み込みに失敗しました。',
  'leaders.noData': 'データなし',
  'leaders.corsNote': 'ライブカテゴリチャートはオプションの Apple RSS プロキシが必要です。この静的ビルドではデフォルトで無効になっています（README 参照）。プロキシを設定するとランキングが表示されます。',
  'leaders.topFree': '無料トップ5',
  'leaders.topPaid': '有料トップ5',

  'export.csv': 'CSVエクスポート',
  'export.report': 'レポートをダウンロード（JSON）',
  'export.reportPrint': 'レポートを印刷',

  'developer.otherApps': '{developer} の他のアプリ',
  'developer.compare': '比較する',
  'developer.compareAriaLabel': '{name} を比較する',

  'compare.toggle': '+ 別のアプリと比較',
  'compare.toggleClose': '− 比較を閉じる',
  'compare.placeholder': '2つ目のアプリ名またはID',
  'compare.button': '比較する',
  'compare.loading': '2つ目のアプリを読み込み中…',
  'compare.clear': 'クリア',
  'compare.col.appA': 'アプリA',
  'compare.col.appB': 'アプリB',
  'compare.col.ratingA': '評価A',
  'compare.col.ratingCountA': 'レビューA',
  'compare.col.ratingB': '評価B',
  'compare.col.ratingCountB': 'レビューB',
  'compare.unavailable': '—',

  'a11y.skipToMain': 'メインコンテンツへスキップ',
  'a11y.opportunityAriaLabel': '先行者優位チャンス：トップ10入りしているにもかかわらずレビューが50件未満',
  'a11y.ratingAbove': 'グローバル平均評価より {value} ポイント高い',
  'a11y.ratingBelow': 'グローバル平均評価より {value} ポイント低い',
  'a11y.openStoreAriaLabel': '{country} App Store を開く',
  'a11y.watchlistCard': '{name} の比較を表示',

  'versionNotes': 'バージョン情報',
  'allSameVersion': 'すべてのストアで v{version} を配信中',
  'versionDifferences': 'ストアごとのバージョン差異：',
  'staggeredRollout': '段階的ロールアウト',
  'staggeredRolloutDetail': 'ほとんどのストアは v{version}。{count} 件は別ビルドです。',
  'releaseNotesDiff': 'リリースノートの差異',
  'releaseNotesCommon': 'ほとんどのストア：{notes}',

  'explore.title': '探索する',
  'explore.subtitle': 'カードをタップして {countryCount} か国の App Store で比較しましょう。',
  'explore.theme.global': 'グローバル大手',
  'explore.theme.regional': '特定地域で人気',
  'explore.theme.insight': '注目の視点',
  'explore.cardAriaLabel': '{name} を各ストアで探索',

  'delta.up': '▲{n}',
  'delta.down': '▼{n}',
  'delta.ariaUp': '{date} 時点より {n} 上昇',
  'delta.ariaDown': '{date} 時点より {n} 下降',
  'delta.firstLook': '初回 — 再度確認すると変化が見られます。',

  'changes.title': '前回の訪問以降の変更',
  'changes.dismiss': '閉じる',
  'changes.version': '{country} でバージョン更新: {prev} → {current}',
  'changes.priceDown': '{country} で値下げ: {prev} → {current}',
  'changes.priceUp': '{country} で値上げ: {prev} → {current}',

  'digest.title': '週間ムーバー',
  'digest.since': '{date} 以降',
  'digest.countriesUp': '{n} か国 ↑',
  'digest.countriesDown': '{n} か国 ↓',

  'marketGap.title': 'チャート浸透率',
  'marketGap.free': '無料トップ: {n}/{total} か国',
  'marketGap.paid': '有料トップ: {n}/{total} か国',
  'marketGap.grossing': '売上トップ: {n}/{total} か国',
  'marketGap.notCharting': '主要市場でチャート圏外:',
  'marketGap.allMajorCharting': '追跡中の主要市場すべてでチャート入り。',

  'marketOpp.title': '市場機会',
  'marketOpp.subtitle': '次に注力すべき市場 — 需要・ローカライズ余地・勢いを統合したシグナルでストアを順位付け。',
  'marketOpp.note': 'Apple の公開シグナルのみに基づくヒューリスティックです。ダウンロード数や収益の推定ではありません。',
  'marketOpp.itemAriaLabel': '{rank}位: {country}、機会スコア100点中{score}点、主要因 {factor}。',
  'marketOpp.factor.demand': '需要',
  'marketOpp.factor.localization': 'ローカライズ余地',
  'marketOpp.factor.momentum': '勢い',
  'marketOpp.factor.visibility': 'チャート露出',
  'marketOpp.factor.volatility': '変動',

  'opportunityRadar.eyebrow': 'Opportunity Radar',
  'opportunityRadar.title': 'チャートシグナルから App 機会を探す',
  'opportunityRadar.subtitle': '日次 App Store チャートキャッシュから生成された候補を確認します。このページは現在読み取り専用で、チャートをリアルタイム更新しません。',
  'opportunityRadar.primaryTargets': '注目範囲',
  'opportunityRadar.secondaryTargets': '拡張範囲',
  'opportunityRadar.targetsAria': 'Opportunity Radar の市場範囲',
  'opportunityRadar.loading': 'キャッシュ済み機会を読み込み中…',
  'opportunityRadar.refreshing': 'キャッシュ更新をリクエスト中です。 ',
  'opportunityRadar.queued': 'キャッシュ更新リクエストをキューに入れました。 ',
  'opportunityRadar.cached': '最新の生成済み機会キャッシュを表示しています。 ',
  'opportunityRadar.generatedAt': '{date} 生成。',
  'opportunityRadar.nextAllowedAt': '次回キャッシュ更新可能: {time}。',
  'opportunityRadar.empty': '生成済みの機会キャッシュはまだありません。このページはブラウザからスキャンせず、日次チャートジョブを待ちます。',
  'opportunityRadar.limited': '現在、キャッシュ更新リクエストはレート制限中です。既存の生成済みデータがある場合は引き続き表示します。',
  'opportunityRadar.paused': 'ストレージ、予算、または安全制限を満たせないため、機会キャッシュの更新は一時停止中です。',
  'opportunityRadar.error': '機会を読み込めませんでした: {message}。',
  'opportunityRadar.retry': '再試行',
  'opportunityRadar.scanNow': 'キャッシュ更新をリクエスト',
  'opportunityRadar.refresh': 'キャッシュ更新をリクエスト',
  'opportunityRadar.scanning': '更新をリクエスト中…',
  'opportunityRadar.scoreLabel': 'スコア',
  'opportunityRadar.scoreAria': '機会スコア 100 点中 {score}',
  'opportunityRadar.unnamedApp': '名前未設定の App',
  'opportunityRadar.unknownGenre': '不明なカテゴリ',
  'opportunityRadar.targetReadout': '市場読み取り',
  'opportunityRadar.noTargetScores': '市場スコアはまだありません。',
  'opportunityRadar.why': '注目理由',
  'opportunityRadar.noReasons': '説明はまだありません。',
  'opportunityRadar.targetNoReason': '主要理由はまだありません。',
  'opportunityRadar.factor.sourceHeat': 'ソース熱量',
  'opportunityRadar.factor.targetCold': 'カバレッジ差分',
  'opportunityRadar.factor.localizationGap': 'ローカライズ不足',
  'opportunityRadar.factor.substituteWeakness': '代替の弱さ',
  'opportunityRadar.reason.source-heat': '{market} はソース市場の強いチャート熱量に支えられています。',
  'opportunityRadar.reason.trend-velocity': '{market} は新規または上昇中のソース市場の動きと結びついています。',
  'opportunityRadar.reason.target-unavailable': '{market} は未提供、または lookup データに存在しません。',
  'opportunityRadar.reason.target-cold': '{market} はチャート露出と現地評価数がまだ低めです。',
  'opportunityRadar.reason.localization-gap': '{market} は現地ストアフロント言語の対応が不足している可能性があります。',
  'opportunityRadar.reason.weak-substitutes': '{market} の確認済みチャートサンプルには、同カテゴリの強い代替が少なめです。',
  'opportunityRadar.breakoutsAria': '毎日のブレイクアウト候補',
  'opportunityRadar.breakoutsEyebrow': 'キャッシュ済み日次ブレイクアウト',
  'opportunityRadar.breakoutsTitle': '最新チャートキャッシュからの機会候補',
  'opportunityRadar.breakoutsEmpty': '最新の日次キャッシュにブレイクアウト候補はありません。',
  'opportunityRadar.capturedAt': '{date} 取得',
  'opportunityRadar.section.hotNewEntries': '注目の新規ランクイン',
  'opportunityRadar.section.fastClimbers': '急上昇',
  'opportunityRadar.section.steadyClimbers': '継続上昇',
  'opportunityRadar.section.needsBaseline': 'ベースライン追加待ち',
  'opportunityRadar.section.reviewFlags': '確認フラグ',
  'opportunityRadar.baseline.complete': 'ベースライン: top100-to-top100',
  'opportunityRadar.baseline.partial': 'ベースライン: 部分的',
  'opportunityRadar.baseline.insufficient': 'ベースライン: 不足',
  'opportunityRadar.baselineDate': 'ベースライン {date}',
  'opportunityRadar.baselineMissingDate': '前回ベースラインなし',
  'opportunityRadar.partialBaselineWarning': '明日から top100-to-top100 の比較がより信頼できます。',
  'opportunityRadar.insufficientBaselineWarning': '新規ランクインを確定扱いする前に、前回の top100 スナップショットを待ってください。',
  'opportunityRadar.methodology.complete': '現在のチャートを、前回の完全なトップ100チャートと比較しています。新規ランクインは前回チャートに存在しなかった項目です。',
  'opportunityRadar.methodology.partial': '候補はトップ100チャートの動きから抽出していますが、前回の基準はトップ25までです。履歴にない項目を、確定した新規ランクインとしてはまだ扱いません。',
  'opportunityRadar.methodology.insufficient': '比較に使える過去チャートがまだ足りません。前回の完全なトップ100チャートができるまでは、方向性を見る候補として扱います。',
  'opportunityRadar.field.market': '市場',
  'opportunityRadar.field.chartType': 'チャート',
  'opportunityRadar.field.rankMovement': '順位変動',
  'opportunityRadar.field.capturedDate': '取得日',
  'opportunityRadar.field.reviewHint': '確認ヒント',
  'opportunityRadar.field.filterStatus': 'フィルター状態',
  'opportunityRadar.field.filterReasons': 'フィルター理由',
  'opportunityRadar.kind.hotNewEntry': '注目の新規ランクイン',
  'opportunityRadar.kind.fastClimber': '急上昇',
  'opportunityRadar.kind.steadyClimber': '継続上昇',
  'opportunityRadar.kind.multiWindowClimber': '複数期間で上昇',
  'opportunityRadar.kind.newEntry': 'Top100 新規ランクイン',
  'opportunityRadar.chartType.topFree': '無料トップ',
  'opportunityRadar.chartType.topPaid': '有料トップ',
  'opportunityRadar.chartType.topGrossing': '売上トップ',
  'opportunityRadar.rank.newTo': '{current} に新規ランクイン',
  'opportunityRadar.rank.moved': '{previous} から {current}{gain}',
  'opportunityRadar.reviewHint.baseline': '完全な top100 ベースラインが利用可能になってから確認してください。',
  'opportunityRadar.reviewHint.lookup': 'Lookup enrich または評価数の確認が必要です。',
  'opportunityRadar.reviewHint.fastClimber': 'カテゴリ文脈と照らして上昇を確認してください。',
  'opportunityRadar.reviewHint.steadyClimber': '24時間・48時間の継続的な順位変化を確認してください。',
  'opportunityRadar.reviewHint.filtered': '生のシグナルとして保持し、明確な機会として扱わないでください。',
  'opportunityRadar.reviewHint.downranked': '機会として比較する前に手動確認してください。',
  'opportunityRadar.reviewHint.needsReview': '機会レビュー前に、キャッシュ済み文脈を追加確認してください。',
  'opportunityRadar.reviewHint.ready': '手動比較レビューに進めます。',
  'opportunityRadar.filterStatus.candidate': '候補',
  'opportunityRadar.filterStatus.downranked': '降格',
  'opportunityRadar.filterStatus.filtered': 'フィルター済み',
  'opportunityRadar.filterStatus.needs_review': '確認待ち',
  'opportunityRadar.filterCategory.government': '政府・公共サービス',
  'opportunityRadar.filterCategory.banking': '銀行・規制金融',
  'opportunityRadar.filterCategory.local_transit': 'ローカル交通',
  'opportunityRadar.filterCategory.ticketing': 'チケット',
  'opportunityRadar.filterCategory.regional_service': '地域サービス',
  'opportunityRadar.filterCategory.seasonal_event': '季節イベント',
  'opportunityRadar.filterCategory.large_platform': '大規模プラットフォーム・メディア',
  'opportunityRadar.filterCategory.low_confidence': '低信頼度',
  'opportunityRadar.filterReason.government-or-public-service': '政府・公共サービス系の App は強く地域に紐づきます。',
  'opportunityRadar.filterReason.regulated-finance-or-identity': '銀行、保険、税務、本人確認系の App は規制市場の確認が必要です。',
  'opportunityRadar.filterReason.local-transit-or-parking': 'ローカル交通、駐車、通行料金系の App は市場横断で転用しにくい可能性があります。',
  'opportunityRadar.filterReason.ticketing-or-local-admission': 'チケットや入場管理系の App は地域在庫に結びつきがちです。',
  'opportunityRadar.filterReason.region-bound-service': '地域サービスはグローバルな製品ギャップではなく、提供範囲を反映している可能性があります。',
  'opportunityRadar.filterReason.large-platform-or-media': '大規模プラットフォームやメディア App は、個人開発の機会を意味しないチャート支配になりがちです。',
  'opportunityRadar.filterReason.seasonal-or-event-bound': '季節・イベント由来の上昇は短期間で落ちる可能性があります。',
  'opportunityRadar.filterReason.low-confidence-missing-genre-or-rating': 'キャッシュ済みチャートにジャンルまたは評価数の文脈が不足しています。',
  'opportunityRadar.filterReason.none': 'フィルター理由は記録されていません。',
  'opportunityRadar.unknownMarket': '不明な市場',
  'opportunityRadar.unknownChartType': '不明なチャート',
  'opportunityRadar.dailyReportEyebrow': '日次ドッグフードレポート',

  'hero.eyebrow': '市場を比較する開発者へ',
  'hero.headline': 'あらゆるアプリを',
  'hero.headlineEm': '国別 App Store で比較',
  'hero.sub': '1 つの iOS / macOS アプリを検索し、評価・チャート順位・価格・提供状況・機会シグナルを {countryCount} のストアフロントで比較。次にローカライズやローンチを進める市場判断に使えます。',
  'features.eyebrow': '機能ハイライト',
  'features.title': 'App Store 需要を国別に読み解く',
  'features.subtitle': '公開された Apple シグナルを使い、ローンチ・ローカライズ・成長施策の前に市場を比較できます。',
  'features.coverage.title': 'ストアフロント網羅',
  'features.coverage.body': '同じアプリを {countryCount} か国で比較し、評価・価格・提供状況を 1 つの表で確認できます。',
  'features.traction.title': 'トラクションシグナル',
  'features.traction.body': '評価件数、平均差、チャート順位を需要の方向性として使います。ダウンロード数として扱うことはありません。',
  'features.opportunity.title': '市場機会',
  'features.opportunity.body': '露出、需要、ローカライズ余地から、次に狙うべき市場候補を見つけます。',
  'features.charts.title': 'チャート探索',
  'features.charts.body': '国別の最新カテゴリチャートを閲覧し、気になるアプリをそのまま比較ワークフローへ送れます。',
  'features.changes.title': '変更モニタリング',
  'features.changes.body': '再訪時に、ローカル保存されたバージョンと価格の変更をストアフロント横断で確認できます。',
  'features.export.title': '共有できる根拠',
  'features.export.body': 'プロダクト、マーケティング、ローカライズ判断の共有に、比較リンクのコピーや CSV 出力を使えます。',
  'trust.ariaLabel': 'このツールを信頼できる理由',
  'trust.noLogin': 'ログイン不要',
  'trust.local': 'ブラウザ内で動作',
  'trust.publicData': '公開された Apple データのみ',
  'trust.free': '無料で利用可能',

  'categoryTrending.title': 'カテゴリ内訳',
  'categoryTrending.summary': 'このチャートは{genre}が中心です（{topCount}/{totalCount}、{topPct}%）。{distinctGenres} カテゴリが含まれます。',
  'categoryTrending.countLabel': '件数',
  'categoryTrending.percentLabel': '割合',
  'categoryTrending.rowAriaLabel': '{genre}: {totalCount} 件中 {count} 件 ({pct}%)',

  'chartBrowser.browseButton': 'チャートを閲覧',
  'chartBrowser.title': 'チャートを閲覧',
  'chartBrowser.subtitle': '各国でトレンドのアプリを確認 — クリックすると {countryCount} の App Store で比較できます。',
  'chartBrowser.sampleSnapshotNote': '{date} 時点のチャートスナップショットです。',
  'chartBrowser.countryLabel': '国',
  'chartBrowser.chartTypeLabel': 'チャートの種類',
  'chartBrowser.type.top-free': '無料トップ',
  'chartBrowser.type.top-paid': '有料トップ',
  'chartBrowser.type.top-grossing': '売上トップ',
  'chartBrowser.genreLabel': 'ジャンル',
  'chartBrowser.genreAll': 'すべてのジャンル',
  'chartBrowser.hint': '国を選択してチャートを読み込む。',
  'chartBrowser.loading': 'チャートを読み込み中…',
  'chartBrowser.error': 'チャートを読み込めませんでした: {message}',
  'chartBrowser.retry': '再試行',
  'chartBrowser.empty': 'この国のチャートデータが見つかりませんでした。',
  'chartBrowser.cardAriaLabel': '#{rank} {name} — クリックして各ストアで比較',
  'chartBrowser.cardAriaLabelGenre': '#{rank} {name}、{genre} — クリックして各ストアで比較',
  'chartBrowser.compareAgainstCurrent': '現在のアプリと比較',
  'chartBrowser.compareAgainstCurrentAriaLabel': '{name} を現在のアプリと比較',
  'chartBrowser.sortLabel': '並べ替え',
  'chartBrowser.sort.rank': 'チャート順位',
  'chartBrowser.sort.name': 'アプリ名',
  'chartBrowser.sort.artist': '開発元',
  'chartBrowser.sort.genre': 'カテゴリ',
  'chartBrowser.sortDir.asc': '昇順 ↑',
  'chartBrowser.sortDir.desc': '降順 ↓',
  'chartBrowser.exportCsv': 'CSVエクスポート',
  'chartBrowser.exportJson': 'JSONエクスポート',
  'chartBrowser.proxyRequired': 'リアルタイムのチャート閲覧には RSS プロキシ（Cloudflare Worker）のデプロイが必要です。GitHub Pages の静的ビルドでは、CORS の制限により Apple の RSS フィードに直接アクセスできません。',
  'chartBrowser.proxySetupLink': 'RSS プロキシを設定する →',
  'chartBrowser.penetration': 'サンプル {total} か国中 {n} か国でもランクイン',
  'chartBrowser.penetrationTitle': 'サンプル内: {countries}',
  'chartBrowser.rankDelta.new': 'NEW',
  'chartBrowser.rankDelta.ariaNew': '{date} 時点から新規ランクイン',
  'chartBrowser.rankDelta.ariaUp': '{date} 時点からチャート順位が {n} 上昇',
  'chartBrowser.rankDelta.ariaDown': '{date} 時点からチャート順位が {n} 下降',
  'share.copyLabel': 'リンクをシェア',
  'share.copyAriaLabel': '比較リンクをクリップボードにコピー',
  'share.twitterLabel': 'X でシェア',
  'share.twitterAriaLabel': 'X（Twitter）で比較をシェア',
  'share.copied': 'コピーしました！',
  'share.twitterText': '「{appName}」を App Radar でチェック — {countryCount} の App Store 市場で比較できます',
};

/** @type {Catalog} */
const zhHant = {
  'coverage.countryCount': '{countryCount}',
  'tagline': '開發者的跨國 App Store 比較：在 {countryCount} 個國家比較任何 App 的評分、排行榜、價格與市場機會。免費、即時、免登入。',
  'brand.name': 'App Radar',
  'brand.tag': '跨國 App Store 情報',
  'brand.homeAria': 'App Radar — 首頁',
  'lang.label': '語言',
  'theme.label': '主題',
  'theme.system': '系統',
  'theme.light': '淺色',
  'theme.dark': '深色',
  'nav.compare': '比較',
  'nav.radar': '排行榜',
  'nav.opportunities': '機會',
  'nav.dailyInsight': '每日報告',

  'search.label': '應用程式名稱',
  'search.placeholder': '搜尋應用程式，例如 Yelp',
  'search.button': '搜尋',
  'search.region.label': '搜尋商店',
  'search.region.auto': '自動（依語言）',
  'search.genre.label': '類別',
  'search.genre.all': '所有類別',
  'search.genre.active': '{genre}',
  'search.platform.label': '平台',
  'search.platform.ios': 'iOS / iPadOS',
  'search.platform.mac': 'macOS',

  'status.searching': '正在搜尋「{query}」…',
  'status.loadingStorefronts': '正在載入「{name}」的 {count} 個國家 App Store…',
  'status.loadingProgress': '正在載入 {current}/{total} 個國家…',
  'status.openingShared': '正在開啟分享的比較…',
  'status.checkingCharts': '正在查詢排行榜名次…',
  'status.idle': '在上方搜尋應用程式，即可在各國 App Store 之間比較。',
  'offline.cachedBanner': '離線 — 正在顯示上次快取的畫面。',

  'error.searchFailed': '搜尋失敗：{message}。請檢查網路連線後再試一次。',
  'error.noResult': '找不到「{query}」的 App Store 結果。請改用其他名稱。',
  'error.loadById': '無法載入應用程式 {appId}：{message}。',
  'error.noAppId': '找不到 ID 為 {appId} 的 App Store 應用程式。',

  'footer.honesty': '誠實聲明：Apple 不公開下載數，本工具也從不估算下載數。它只使用公開的代理指標 — 平均評分、評分數、價格、是否上架，以及可用的公開排行榜快照。一般比較不需要登入；機會頁讀取已產生的每日快取。',
  'footer.attribution': '資料來自 Apple App Store。',
  'footer.notAffiliated': '與 Apple Inc. 並無隸屬關係。',
  'footer.localFirst': '在地優先 · 一切都在你的瀏覽器中執行',
  'footer.version': '建置',

  'col.region': '地區',
  'col.rating': '評分',
  'col.ratingCount': '評分數',
  'col.ratingDeviation': '與平均差',
  'col.price': '價格',
  'col.chartRank': '排行榜名次',
  'col.popularityScore': '人氣指數',
  'col.highlights': '亮點',
  'col.store': '商店',

  'cell.free': '免費',
  'cell.open': '開啟 ↗',
  'cell.unavailable': '此 App Store 未上架',
  'cell.lookupError': '查詢錯誤：{message}',

  'tag.rating-count-leader': '聲量最高',
  'tag.rating-count-low': '聲量最低',
  'tag.rating-leader': '評分最高',
  'tag.rating-low': '評分最低',
  'tag.emerging': '崛起中',
  'tag.low-visibility': '能見度低',
  'tag.price-mismatch': '價格異常',
  'tag.localization-gap': '缺 {language}',
  'tag.localization-gap.tooltip': 'Apple languageCodesISO2A 未列出此商店的 {language}。',
  'tag.unavailable': '未上架',
  'opportunity.label': '先行者機會',
  'opportunity.tooltip': '排名前十但評分不足50則 — 可能存在先行者機會',

  'insight.most': '聲量最高：{region}',
  'insight.weakest': '最弱：{region}',
  'insight.suffix': '評分數是人氣的代理指標，並非下載數。',
  'insight.none': '沒有可供跨地區比較的評分資料。',

  'chartNote.enabled': '排行榜名次是 Apple 公布的免費／付費排行榜名次，並非下載數。',
  'chartNote.disabled': '此靜態版本停用排行榜名次：Apple 的 RSS feed 無法從瀏覽器直接存取（CORS）。啟用內建代理即可顯示即時名次（見 README）。',
  'chart.disabledTooltip': '即時排行榜名次需要 RSS 代理 — 此版本已停用（見 config.js）',
  'chart.notLoaded': '尚未載入排行榜名次',
  'chart.unavailableTooltip': '無法讀取 Apple 的 RSS feed',

  'table.caption': '各地區比較。長條顯示每個地區的評分數佔目前所顯示最高值的比例 — 這是人氣的代理指標，並非下載數。Apple 不公開下載數。',
  'proxyBar.title': '目前所顯示最高評分數的 {pct}%',

  ...storefrontCountryMessages,
  'country.us': '美國',
  'country.de': '德國',
  'country.gb': '英國',
  'country.ca': '加拿大',
  'country.au': '澳洲',
  'country.fr': '法國',
  'country.it': '義大利',
  'country.es': '西班牙',
  'country.nl': '荷蘭',
  'country.se': '瑞典',
  'country.pl': '波蘭',
  'country.ru': '俄羅斯',
  'country.jp': '日本',
  'country.kr': '南韓',
  'country.cn': '中國大陸',
  'country.hk': '香港',
  'country.tw': '台灣',
  'country.sg': '新加坡',
  'country.id': '印尼',
  'country.th': '泰國',
  'country.my': '馬來西亞',
  'country.in': '印度',
  'country.sa': '沙烏地阿拉伯',
  'country.ae': '阿聯酋',
  'country.br': '巴西',
  'country.mx': '墨西哥',
  'country.ar': '阿根廷',
  'country.za': '南非',

  'ranking.title': '按評分數排名的熱門市場',
  'leaderboard.title': '最近搜尋',
  'watchlist.title': '已儲存的 App',
  'watchlist.pin': '☆ 儲存',
  'watchlist.unpin': '★ 已儲存',

  'leaders.panelTitle': '類別排行榜 — {genre}',
  'leaders.selectCountry': '選擇國家',
  'leaders.loading': '正在載入類別排行…',
  'leaders.loadError': '無法載入類別排行資料。',
  'leaders.noData': '無資料',
  'leaders.corsNote': '即時類別排行需要選用的 Apple RSS Proxy，此靜態建置預設關閉（請參閱 README）。設定 Proxy 後即可顯示排行。',
  'leaders.topFree': '免費前5名',
  'leaders.topPaid': '付費前5名',

  'export.csv': '匯出 CSV',
  'export.report': '下載報告（JSON）',
  'export.reportPrint': '列印報告',

  'developer.otherApps': '{developer} 的其他 App',
  'developer.compare': '比較',
  'developer.compareAriaLabel': '比較 {name}',

  'compare.toggle': '+ 比較另一個 App',
  'compare.toggleClose': '− 關閉比較',
  'compare.placeholder': '第二個應用程式名稱或 ID',
  'compare.button': '比較',
  'compare.loading': '正在載入第二個 App…',
  'compare.clear': '清除',
  'compare.col.appA': 'App A',
  'compare.col.appB': 'App B',
  'compare.col.ratingA': '評分 A',
  'compare.col.ratingCountA': '評分數 A',
  'compare.col.ratingB': '評分 B',
  'compare.col.ratingCountB': '評分數 B',
  'compare.unavailable': '—',

  'a11y.skipToMain': '跳至主要內容',
  'a11y.opportunityAriaLabel': '先行者機會：排名前十但評分不足50則',
  'a11y.ratingAbove': '比全球平均評分高 {value} 分',
  'a11y.ratingBelow': '比全球平均評分低 {value} 分',
  'a11y.openStoreAriaLabel': '開啟 {country} App Store 頁面',
  'a11y.watchlistCard': '顯示 {name} 的比較',

  'versionNotes': '版本資訊',
  'allSameVersion': '所有商店均執行 v{version}',
  'versionDifferences': '各商店版本差異：',
  'staggeredRollout': '分階段推出',
  'staggeredRolloutDetail': '多數商店為 v{version}；{count} 個為其他版本。',
  'releaseNotesDiff': '版本說明差異',
  'releaseNotesCommon': '多數商店：{notes}',

  'explore.title': '探索',
  'explore.subtitle': '點擊任一卡片，即可在 {countryCount} 個國家 App Store 之間比較。',
  'explore.theme.global': '全球巨頭',
  'explore.theme.regional': '特定地區熱門',
  'explore.theme.insight': '有趣的視角',
  'explore.cardAriaLabel': '在各商店探索 {name}',

  'delta.up': '▲{n}',
  'delta.down': '▼{n}',
  'delta.ariaUp': '較 {date} 上升 {n}',
  'delta.ariaDown': '較 {date} 下降 {n}',
  'delta.firstLook': '首次查詢 — 再次查閱可看到變化。',

  'changes.title': '自上次造訪以來的變更',
  'changes.dismiss': '關閉',
  'changes.version': '{country} 版本更新：{prev} → {current}',
  'changes.priceDown': '{country} 降價：{prev} → {current}',
  'changes.priceUp': '{country} 漲價：{prev} → {current}',

  'digest.title': '每週移動榜',
  'digest.since': '對比 {date}',
  'digest.countriesUp': '{n} 個國家 ↑',
  'digest.countriesDown': '{n} 個國家 ↓',

  'marketGap.title': '排行榜滲透率',
  'marketGap.free': '免費榜: {n}/{total} 個國家',
  'marketGap.paid': '付費榜: {n}/{total} 個國家',
  'marketGap.grossing': '暢銷榜: {n}/{total} 個國家',
  'marketGap.notCharting': '在以下主要市場未上榜:',
  'marketGap.allMajorCharting': '在所有追蹤的主要市場均已上榜。',

  'marketOpp.title': '市場機會',
  'marketOpp.subtitle': '下一步該主攻哪裡 — 綜合需求、在地化空間與成長動能為各商店排序。',
  'marketOpp.note': '僅依據 Apple 公開訊號的啟發式評估，並非下載量或營收估計。',
  'marketOpp.itemAriaLabel': '第 {rank} 名：{country}，機會分數 {score}（滿分 100），主要因素 {factor}。',
  'marketOpp.factor.demand': '需求',
  'marketOpp.factor.localization': '在地化空間',
  'marketOpp.factor.momentum': '成長動能',
  'marketOpp.factor.visibility': '排行榜曝光',
  'marketOpp.factor.volatility': '波動',

  'opportunityRadar.eyebrow': 'Opportunity Radar',
  'opportunityRadar.title': '尋找由排行榜訊號驅動的 App 機會',
  'opportunityRadar.subtitle': '查看由每日 App Store 排行榜快取產生的機會候選。此頁面目前只讀，不會即時刷新排行榜。',
  'opportunityRadar.primaryTargets': '核心範圍',
  'opportunityRadar.secondaryTargets': '延伸範圍',
  'opportunityRadar.targetsAria': 'Opportunity Radar 市場範圍',
  'opportunityRadar.loading': '正在載入快取機會…',
  'opportunityRadar.refreshing': '正在請求更新快取。 ',
  'opportunityRadar.queued': '快取更新請求已排入佇列。 ',
  'opportunityRadar.cached': '正在顯示最新產生的機會快取。 ',
  'opportunityRadar.generatedAt': '{date} 產生。',
  'opportunityRadar.nextAllowedAt': '下次可更新快取時間：{time}。',
  'opportunityRadar.empty': '目前還沒有已產生的機會快取。此頁面會等待每日排行榜任務，而不是從瀏覽器掃描。',
  'opportunityRadar.limited': '目前快取更新請求受到頻率限制。已有的產生資料仍會顯示。',
  'opportunityRadar.paused': '由於儲存、預算或安全限制不可用，機會快取更新已暫停。',
  'opportunityRadar.error': '無法載入機會：{message}。',
  'opportunityRadar.retry': '重試',
  'opportunityRadar.scanNow': '請求更新快取',
  'opportunityRadar.refresh': '請求更新快取',
  'opportunityRadar.scanning': '正在請求更新…',
  'opportunityRadar.scoreLabel': '分數',
  'opportunityRadar.scoreAria': '機會分數 {score}/100',
  'opportunityRadar.unnamedApp': '未命名 App',
  'opportunityRadar.unknownGenre': '未知類別',
  'opportunityRadar.targetReadout': '市場讀數',
  'opportunityRadar.noTargetScores': '尚無市場分數。',
  'opportunityRadar.why': '為什麼值得看',
  'opportunityRadar.noReasons': '尚無解釋。',
  'opportunityRadar.targetNoReason': '尚無主要原因。',
  'opportunityRadar.factor.sourceHeat': '來源熱度',
  'opportunityRadar.factor.targetCold': '覆蓋缺口',
  'opportunityRadar.factor.localizationGap': '在地化缺口',
  'opportunityRadar.factor.substituteWeakness': '替代品弱度',
  'opportunityRadar.reason.source-heat': '{market} 受益於來源市場的強排行榜熱度。',
  'opportunityRadar.reason.trend-velocity': '{market} 連到新進或上升中的來源市場動能。',
  'opportunityRadar.reason.target-unavailable': '{market} 未上架，或 lookup 資料中不存在。',
  'opportunityRadar.reason.target-cold': '{market} 的排行榜露出和本地評分量仍偏低。',
  'opportunityRadar.reason.localization-gap': '{market} 可能缺少本地商店語言支援。',
  'opportunityRadar.reason.weak-substitutes': '{market} 的已檢查排行榜樣本中，強同類替代品較少。',
  'opportunityRadar.breakoutsAria': '每日突破候選',
  'opportunityRadar.breakoutsEyebrow': '已快取的每日突破',
  'opportunityRadar.breakoutsTitle': '最新排行榜快取中的機會候選',
  'opportunityRadar.breakoutsEmpty': '最新每日快取中沒有突破候選。',
  'opportunityRadar.capturedAt': '{date} 擷取',
  'opportunityRadar.section.hotNewEntries': '熱門新進榜',
  'opportunityRadar.section.fastClimbers': '快速上升',
  'opportunityRadar.section.steadyClimbers': '穩定上升',
  'opportunityRadar.section.needsBaseline': '需要更多基線',
  'opportunityRadar.section.reviewFlags': '複核標記',
  'opportunityRadar.baseline.complete': '基線：已可對比完整前 100',
  'opportunityRadar.baseline.partial': '基線：部分',
  'opportunityRadar.baseline.insufficient': '基線：不足',
  'opportunityRadar.baselineDate': '基線 {date}',
  'opportunityRadar.baselineMissingDate': '沒有前次基線',
  'opportunityRadar.partialBaselineWarning': '明天開始用完整前 100 對比，會更可靠。',
  'opportunityRadar.insufficientBaselineWarning': '等到有前一次完整前 100 榜單後，再把新進榜視為已確認。',
  'opportunityRadar.methodology.complete': '目前排行榜已和前一次完整前 100 榜單對比；新進代表前一次沒有出現在榜內。',
  'opportunityRadar.methodology.partial': '這些候選來自前 100 榜單變化，但前一次基線只覆蓋前 25。缺失的歷史還不能視為已確認的新進榜。',
  'opportunityRadar.methodology.insufficient': '目前還沒有足夠的前次榜單可比較。等到有前一次完整前 100 榜單前，只把它當作方向性候選。',
  'opportunityRadar.field.market': '市場',
  'opportunityRadar.field.chartType': '排行榜',
  'opportunityRadar.field.rankMovement': '排名變化',
  'opportunityRadar.field.capturedDate': '擷取日期',
  'opportunityRadar.field.reviewHint': '複核提示',
  'opportunityRadar.field.filterStatus': '過濾狀態',
  'opportunityRadar.field.filterReasons': '過濾原因',
  'opportunityRadar.kind.hotNewEntry': '熱門新進榜',
  'opportunityRadar.kind.fastClimber': '快速上升',
  'opportunityRadar.kind.steadyClimber': '穩定上升',
  'opportunityRadar.kind.multiWindowClimber': '多窗口上升',
  'opportunityRadar.kind.newEntry': '前 100 新進榜',
  'opportunityRadar.chartType.topFree': '免費榜',
  'opportunityRadar.chartType.topPaid': '付費榜',
  'opportunityRadar.chartType.topGrossing': '暢銷榜',
  'opportunityRadar.rank.newTo': '新進 {current}',
  'opportunityRadar.rank.moved': '{previous} 到 {current}{gain}',
  'opportunityRadar.reviewHint.baseline': '完整前 100 基線可用後再複核。',
  'opportunityRadar.reviewHint.lookup': '需要複核查詢補充資訊或評分量。',
  'opportunityRadar.reviewHint.fastClimber': '請對照類別脈絡確認上升幅度。',
  'opportunityRadar.reviewHint.steadyClimber': '請複核 24 小時與 48 小時窗口中的連續排名變化。',
  'opportunityRadar.reviewHint.filtered': '保留為原始訊號；不要當成明確機會。',
  'opportunityRadar.reviewHint.downranked': '作為機會比較前，請先人工複核。',
  'opportunityRadar.reviewHint.needsReview': '進入機會複核前，需要更多快取脈絡。',
  'opportunityRadar.reviewHint.ready': '可進入人工比較複核。',
  'opportunityRadar.filterStatus.candidate': '候選',
  'opportunityRadar.filterStatus.downranked': '降權',
  'opportunityRadar.filterStatus.filtered': '已過濾',
  'opportunityRadar.filterStatus.needs_review': '待複核',
  'opportunityRadar.filterCategory.government': '政府或公共服務',
  'opportunityRadar.filterCategory.banking': '銀行或受監管金融',
  'opportunityRadar.filterCategory.local_transit': '本地交通',
  'opportunityRadar.filterCategory.ticketing': '票務',
  'opportunityRadar.filterCategory.regional_service': '區域服務',
  'opportunityRadar.filterCategory.seasonal_event': '季節事件',
  'opportunityRadar.filterCategory.large_platform': '大型平台或媒體',
  'opportunityRadar.filterCategory.low_confidence': '低信心',
  'opportunityRadar.filterReason.government-or-public-service': '政府或公共服務 App 通常強烈綁定本地。',
  'opportunityRadar.filterReason.regulated-finance-or-identity': '銀行、保險、稅務或身份類 App 需要受監管市場複核。',
  'opportunityRadar.filterReason.local-transit-or-parking': '本地交通、停車或道路收費 App 可能難以跨市場遷移。',
  'opportunityRadar.filterReason.ticketing-or-local-admission': '票務與入場類 App 常綁定本地供給。',
  'opportunityRadar.filterReason.region-bound-service': '區域服務可能反映覆蓋範圍，而不是全球產品缺口。',
  'opportunityRadar.filterReason.large-platform-or-media': '大型平台或媒體 App 可能因規模佔榜，不代表個人開發機會。',
  'opportunityRadar.filterReason.seasonal-or-event-bound': '季節或事件帶來的上升可能很快衰退。',
  'opportunityRadar.filterReason.low-confidence-missing-genre-or-rating': '快取排行榜缺少足夠類別或評分量脈絡。',
  'opportunityRadar.filterReason.none': '沒有記錄過濾原因。',
  'opportunityRadar.unknownMarket': '未知市場',
  'opportunityRadar.unknownChartType': '未知排行榜',
  'opportunityRadar.dailyReportEyebrow': '每日內部試用報告',

  'hero.eyebrow': '為比較市場的開發者打造',
  'hero.headline': '將任何 App 放到',
  'hero.headlineEm': '各國 App Store 比較',
  'hero.sub': '搜尋一個 iOS 或 macOS App，即可在 {countryCount} 個商店比較評分、排行榜位置、價格、供應狀態與機會訊號，協助開發者決定下一個在地化或上市市場。',
  'features.eyebrow': '功能重點',
  'features.title': '用跨國視角讀懂 App Store 需求',
  'features.subtitle': '使用 Apple 公開訊號，在決定上市、在地化或成長投入前比較各個市場。',
  'features.coverage.title': '商店覆蓋',
  'features.coverage.body': '在 {countryCount} 個國家比較同一個 App，將評分、價格與供應狀態對齊在同一張表。',
  'features.traction.title': '熱度訊號',
  'features.traction.body': '以評分數、評分差異與排行榜名次作為需求方向訊號，但不把它們當成下載量。',
  'features.opportunity.title': '市場機會',
  'features.opportunity.body': '從曝光、需求與在地化空間找出更適合作為下一個目標的市場。',
  'features.charts.title': '排行榜探索',
  'features.charts.body': '依國家瀏覽目前的類別排行榜，再將任何 App 送進完整比較流程。',
  'features.changes.title': '變更監測',
  'features.changes.body': '再次查看 App 時，可看到本機儲存的版本與價格變更在各商店的差異。',
  'features.export.title': '可分享的依據',
  'features.export.body': '需要向產品、行銷或在地化決策說明時，可複製比較連結或匯出 CSV。',
  'trust.ariaLabel': '這個工具值得信任的原因',
  'trust.noLogin': '免登入',
  'trust.local': '在你的瀏覽器中執行',
  'trust.publicData': '僅使用公開的 Apple 資料',
  'trust.free': '免費使用',

  'categoryTrending.title': '類別分佈',
  'categoryTrending.summary': '此排行榜以{genre}為主（{topCount}/{totalCount}，{topPct}%）；共有 {distinctGenres} 個類別。',
  'categoryTrending.countLabel': '數量',
  'categoryTrending.percentLabel': '占比',
  'categoryTrending.rowAriaLabel': '{genre}：{count}/{totalCount}（{pct}%）',

  'chartBrowser.browseButton': '瀏覽排行榜',
  'chartBrowser.title': '瀏覽排行榜',
  'chartBrowser.subtitle': '查看各國的熱門 App — 點擊任一 App 即可在 {countryCount} 個國家 App Store 中比較。',
  'chartBrowser.sampleSnapshotNote': '{date} 的排行榜快照。',
  'chartBrowser.countryLabel': '國家',
  'chartBrowser.chartTypeLabel': '排行榜類型',
  'chartBrowser.type.top-free': '免費榜',
  'chartBrowser.type.top-paid': '付費榜',
  'chartBrowser.type.top-grossing': '暢銷榜',
  'chartBrowser.genreLabel': '類型',
  'chartBrowser.genreAll': '所有類型',
  'chartBrowser.hint': '選擇國家以載入目前排行榜。',
  'chartBrowser.loading': '載入排行榜中…',
  'chartBrowser.error': '無法載入排行榜：{message}',
  'chartBrowser.retry': '重試',
  'chartBrowser.empty': '此國家沒有排行榜資料。',
  'chartBrowser.cardAriaLabel': '第 {rank} 名 {name} — 點擊以在各商店比較',
  'chartBrowser.cardAriaLabelGenre': '第 {rank} 名 {name}，{genre} — 點擊以在各商店比較',
  'chartBrowser.compareAgainstCurrent': '與目前 App 比較',
  'chartBrowser.compareAgainstCurrentAriaLabel': '將 {name} 與目前 App 比較',
  'chartBrowser.sortLabel': '排序方式',
  'chartBrowser.sort.rank': '排行榜名次',
  'chartBrowser.sort.name': 'App 名稱',
  'chartBrowser.sort.artist': '開發者',
  'chartBrowser.sort.genre': '類別',
  'chartBrowser.sortDir.asc': '遞增 ↑',
  'chartBrowser.sortDir.desc': '遞減 ↓',
  'chartBrowser.exportCsv': '匯出 CSV',
  'chartBrowser.exportJson': '匯出 JSON',
  'chartBrowser.proxyRequired': '即時排行榜瀏覽需要部署 RSS 代理（Cloudflare Worker）。GitHub Pages 靜態版本因 CORS 限制，無法直接存取 Apple 的 RSS feed。',
  'chartBrowser.proxySetupLink': '設定 RSS 代理 →',
  'chartBrowser.penetration': '在你的 {total} 個樣本國家中有 {n} 個上榜',
  'chartBrowser.penetrationTitle': '樣本中：{countries}',
  'chartBrowser.rankDelta.new': 'NEW',
  'chartBrowser.rankDelta.ariaNew': '自 {date} 檢查後新進榜',
  'chartBrowser.rankDelta.ariaUp': '自 {date} 檢查後排行榜上升 {n} 名',
  'chartBrowser.rankDelta.ariaDown': '自 {date} 檢查後排行榜下降 {n} 名',
  'share.copyLabel': '分享連結',
  'share.copyAriaLabel': '複製比較連結到剪貼簿',
  'share.twitterLabel': '分享到 X',
  'share.twitterAriaLabel': '在 X（Twitter）分享比較',
  'share.copied': '已複製！',
  'share.twitterText': '在 App Radar 查看「{appName}」於 {countryCount} 個 App Store 市場的比較',
};

/** @type {Catalog} */
const zhCN = simplifyCatalog(zhHant, {
  'brand.name': 'App Radar',
  'brand.tag': 'App Store 市场雷达',
  'brand.homeAria': 'App Radar 首页',
  'theme.system': '系统',
  'nav.compare': '比较',
  'nav.radar': '排行榜',
  'nav.opportunities': '机会',
  'nav.dailyInsight': '每日报告',
  'tagline': '面向开发者的跨国家 App Store 比较：按评分、排行榜、价格和市场机会，在 {countryCount} 个国家比较任何 App。免费、即时、免登录。',
  'search.placeholder': '搜索 App，例如 Yelp',
  'status.idle': '在上方搜索 App，即可比较它在各国 App Store 的表现。',
  'footer.honesty': '透明说明：Apple 不公开下载量，本工具也从不估算下载量。它只使用公开代理指标：平均评分、评分数、价格、是否上架，以及可用的公开排行榜快照。普通比较不需要登录；机会页读取已生成的每日缓存。',
  'footer.localFirst': '直接在浏览器中运行',
  'cell.unavailable': '此 App Store 未上架',
  'opportunity.tooltip': '进入前 10 但评分少于 50 条，可能存在先发市场窗口',
  'chartNote.enabled': '排行榜名次是 Apple 发布的免费榜/付费榜名次，并非下载量。',
  'chartNote.disabled': '此静态版本停用排行榜名次：Apple 的 RSS feed 无法从浏览器直接读取（CORS）。启用内置代理即可显示实时名次（见 README）。',
  'table.caption': '各地区比较。横条显示每个地区的评分数占当前最高评分数的比例，这是人气代理指标，不是下载量。Apple 不公开下载量。',
  'export.report': '下载报告（JSON）',
  'compare.placeholder': '第二个 App 名称或 ID',
  'versionNotes': '版本信息',
  'explore.subtitle': '点击任一卡片，即可在 {countryCount} 个国家 App Store 中比较。',
  'marketGap.title': '排行榜覆盖率',
  'marketOpp.subtitle': '下一步该聚焦哪里：按需求、本地化空间和动量的综合信号，为各商店排序。',
  'marketOpp.note': '仅基于 Apple 公开信号的启发式评估，不是下载量或收入估算。',
  'opportunityRadar.eyebrow': '机会雷达',
  'opportunityRadar.title': '寻找由榜单信号驱动的 App 机会',
  'opportunityRadar.subtitle': '查看由每日 App Store 排行榜缓存生成的机会候选。这个页面目前只读，不会实时刷新榜单。',
  'opportunityRadar.primaryTargets': '核心范围',
  'opportunityRadar.secondaryTargets': '扩展范围',
  'opportunityRadar.targetsAria': '机会雷达市场范围',
  'opportunityRadar.loading': '正在加载缓存机会…',
  'opportunityRadar.refreshing': '正在请求更新缓存。 ',
  'opportunityRadar.queued': '缓存更新请求已进入队列。 ',
  'opportunityRadar.cached': '正在显示最新生成的机会缓存。 ',
  'opportunityRadar.generatedAt': '{date} 生成。',
  'opportunityRadar.nextAllowedAt': '下次可更新缓存时间：{time}。',
  'opportunityRadar.empty': '暂无已生成的机会缓存。这个页面会等待每日榜单任务，不会由浏览器发起扫描。',
  'opportunityRadar.limited': '当前缓存更新请求受到频率限制。已有的生成数据仍会显示。',
  'opportunityRadar.paused': '由于存储、预算或安全限制不可用，机会缓存更新已暂停。',
  'opportunityRadar.error': '无法加载机会：{message}。',
  'opportunityRadar.retry': '重试',
  'opportunityRadar.scanNow': '请求更新缓存',
  'opportunityRadar.refresh': '请求更新缓存',
  'opportunityRadar.scanning': '正在请求更新…',
  'opportunityRadar.scoreLabel': '分数',
  'opportunityRadar.scoreAria': '机会分数 {score}/100',
  'opportunityRadar.unnamedApp': '未命名 App',
  'opportunityRadar.unknownGenre': '未知类别',
  'opportunityRadar.targetReadout': '市场读数',
  'opportunityRadar.noTargetScores': '暂无市场分数。',
  'opportunityRadar.why': '为什么值得看',
  'opportunityRadar.noReasons': '暂无解释。',
  'opportunityRadar.targetNoReason': '暂无主要原因。',
  'opportunityRadar.factor.sourceHeat': '源市场热度',
  'opportunityRadar.factor.targetCold': '覆盖缺口',
  'opportunityRadar.factor.localizationGap': '本地化缺口',
  'opportunityRadar.factor.substituteWeakness': '替代品弱度',
  'opportunityRadar.reason.source-heat': '{market} 受益于源市场的强排行榜热度。',
  'opportunityRadar.reason.trend-velocity': '{market} 关联到新进或上升中的源市场动量。',
  'opportunityRadar.reason.target-unavailable': '{market} 未上架，或查询数据中不存在。',
  'opportunityRadar.reason.target-cold': '{market} 的排行榜露出和本地评分量仍偏低。',
  'opportunityRadar.reason.localization-gap': '{market} 可能缺少本地商店语言支持。',
  'opportunityRadar.reason.weak-substitutes': '{market} 的已检查排行榜样本中，强同类替代品较少。',
  'opportunityRadar.breakoutsAria': '每日突破候选',
  'opportunityRadar.breakoutsEyebrow': '每日机会候选',
  'opportunityRadar.breakoutsTitle': '最新排行榜缓存中的机会候选',
  'opportunityRadar.breakoutsEmpty': '最新每日缓存中没有机会候选。',
  'opportunityRadar.capturedAt': '{date} 采集',
  'opportunityRadar.section.hotNewEntries': '高位新进榜',
  'opportunityRadar.section.fastClimbers': '快速上升',
  'opportunityRadar.section.steadyClimbers': '稳定上升',
  'opportunityRadar.section.needsBaseline': '等待完整基线',
  'opportunityRadar.section.reviewFlags': '复核标记',
  'opportunityRadar.baseline.complete': '基线：已可对比完整前 100',
  'opportunityRadar.baseline.partial': '基线：部分可用',
  'opportunityRadar.baseline.insufficient': '基线：不足',
  'opportunityRadar.baselineDate': '基线 {date}',
  'opportunityRadar.baselineMissingDate': '没有前次基线',
  'opportunityRadar.partialBaselineWarning': '明天开始用完整前 100 对比，会更可靠。',
  'opportunityRadar.insufficientBaselineWarning': '等到有前一次完整前 100 榜单后，再把新进榜视为已确认。',
  'opportunityRadar.methodology.complete': '当前榜单已和前一次完整前 100 榜单对比；新进表示前一次没有出现在榜内。',
  'opportunityRadar.methodology.partial': '这些候选来自前 100 榜单变化，但前一次基线只覆盖前 25。缺失的历史还不能当作真正新进榜。',
  'opportunityRadar.methodology.insufficient': '目前还没有足够的前次榜单可比较。等到有前一次完整前 100 榜单前，只把它当作方向性候选。',
  'opportunityRadar.field.market': '市场',
  'opportunityRadar.field.chartType': '排行榜',
  'opportunityRadar.field.rankMovement': '排名变化',
  'opportunityRadar.field.capturedDate': '采集日期',
  'opportunityRadar.field.reviewHint': '复核提示',
  'opportunityRadar.field.filterStatus': '过滤状态',
  'opportunityRadar.field.filterReasons': '过滤原因',
  'opportunityRadar.kind.hotNewEntry': '热门新进榜',
  'opportunityRadar.kind.fastClimber': '快速上升',
  'opportunityRadar.kind.steadyClimber': '稳定上升',
  'opportunityRadar.kind.multiWindowClimber': '多窗口上升',
  'opportunityRadar.kind.newEntry': '前 100 新进榜',
  'opportunityRadar.chartType.topFree': '免费榜',
  'opportunityRadar.chartType.topPaid': '付费榜',
  'opportunityRadar.chartType.topGrossing': '畅销榜',
  'opportunityRadar.rank.newTo': '新进 {current}',
  'opportunityRadar.rank.moved': '{previous} 到 {current}{gain}',
  'opportunityRadar.reviewHint.baseline': '完整前 100 基线可用后再复核。',
  'opportunityRadar.reviewHint.lookup': '需要复核查询补充信息或评分量。',
  'opportunityRadar.reviewHint.fastClimber': '请对照类别语境确认上升幅度。',
  'opportunityRadar.reviewHint.steadyClimber': '请复核 24 小时与 48 小时窗口中的连续排名变化。',
  'opportunityRadar.reviewHint.filtered': '保留为原始信号；不要当成明确机会。',
  'opportunityRadar.reviewHint.downranked': '作为机会比较前，请先人工复核。',
  'opportunityRadar.reviewHint.needsReview': '进入机会复核前，需要更多缓存语境。',
  'opportunityRadar.reviewHint.ready': '可进入人工比较复核。',
  'opportunityRadar.filterStatus.candidate': '候选',
  'opportunityRadar.filterStatus.downranked': '降权',
  'opportunityRadar.filterStatus.filtered': '已过滤',
  'opportunityRadar.filterStatus.needs_review': '待复核',
  'opportunityRadar.filterCategory.government': '政府或公共服务',
  'opportunityRadar.filterCategory.banking': '银行或受监管金融',
  'opportunityRadar.filterCategory.local_transit': '本地交通',
  'opportunityRadar.filterCategory.ticketing': '票务',
  'opportunityRadar.filterCategory.regional_service': '区域服务',
  'opportunityRadar.filterCategory.seasonal_event': '季节事件',
  'opportunityRadar.filterCategory.large_platform': '大型平台或媒体',
  'opportunityRadar.filterCategory.low_confidence': '低置信度',
  'opportunityRadar.filterReason.government-or-public-service': '政府或公共服务 App 通常强烈绑定本地。',
  'opportunityRadar.filterReason.regulated-finance-or-identity': '银行、保险、税务或身份类 App 需要受监管市场复核。',
  'opportunityRadar.filterReason.local-transit-or-parking': '本地交通、停车或道路收费 App 可能难以跨市场迁移。',
  'opportunityRadar.filterReason.ticketing-or-local-admission': '票务与入场类 App 常绑定本地供给。',
  'opportunityRadar.filterReason.region-bound-service': '区域服务可能反映覆盖范围，而不是全球产品缺口。',
  'opportunityRadar.filterReason.large-platform-or-media': '大型平台或媒体 App 可能因规模占榜，不代表个人开发机会。',
  'opportunityRadar.filterReason.seasonal-or-event-bound': '季节或事件带来的上升可能很快衰退。',
  'opportunityRadar.filterReason.low-confidence-missing-genre-or-rating': '缓存排行榜缺少足够类别或评分量语境。',
  'opportunityRadar.filterReason.none': '没有记录过滤原因。',
  'opportunityRadar.unknownMarket': '未知市场',
  'opportunityRadar.unknownChartType': '未知排行榜',
  'opportunityRadar.dailyReportEyebrow': '每日内部试用报告',
  'hero.eyebrow': '为比较市场的开发者打造',
  'hero.headline': '将任何 App 放到',
  'hero.headlineEm': '各国 App Store 比较',
  'hero.sub': '搜索一个 iOS 或 macOS App，即可在 {countryCount} 个商店比较评分、排行榜位置、价格、上架状态和机会信号，帮助开发者决定下一步本地化或发布市场。',
  'features.title': '用跨国视角读懂 App Store 需求',
  'features.subtitle': '使用 Apple 公开信号，在决定发布、本地化或增长投入前比较各个市场。',
  'features.coverage.body': '在 {countryCount} 个国家比较同一个 App，将评分、价格和上架状态对齐在同一张表。',
  'features.traction.body': '用评分数、评分差异和排行榜名次判断需求方向，但不把它们当成下载量。',
  'features.opportunity.body': '从曝光、需求和本地化空间中找出更适合作为下一目标的市场。',
  'features.export.body': '需要向产品、营销或本地化决策说明时，可复制比较链接或导出 CSV。',
  'trust.local': '在你的浏览器中运行',
  'trust.free': '免费使用，免登录',
  'chartBrowser.subtitle': '查看各国的热门 App，点击任一 App 即可在 {countryCount} 个国家 App Store 中比较。',
  'chartBrowser.cardAriaLabel': '第 {rank} 名 {name}，点击以在各商店比较',
  'chartBrowser.cardAriaLabelGenre': '第 {rank} 名 {name}，{genre}，点击以在各商店比较',
  'chartBrowser.proxyRequired': '实时排行榜浏览需要部署 RSS 代理（Cloudflare Worker）。GitHub Pages 静态版本因 CORS 限制，无法直接访问 Apple 的 RSS feed。',
  'share.twitterText': '在 App Radar 查看“{appName}”于 {countryCount} 个 App Store 市场的比较',
});

export const MESSAGES = Object.freeze({
  en,
  ja,
  'zh-CN': zhCN,
  'zh-Hant': zhHant,
});

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = Object.freeze(['en', 'ja', 'zh-CN', 'zh-Hant']);

// Native display names for each locale, shown in the language switcher.
// Intentionally not translated — a language is labelled in its own language.
export const LOCALE_NAMES = Object.freeze({
  en: 'English',
  ja: '日本語',
  'zh-CN': '简体中文',
  'zh-Hant': '繁體中文',
});
